import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, ChevronDown, LogOut } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";

const PROJECT_LINKS = [
  { to: "/", label: "Overview" },
  { to: "/seza", label: "SEZA & Government" },
  { to: "/supply-chain", label: "Supply Chain" },
  { to: "/execution", label: "Execution" },
];

const CAPITAL_LINKS = [
  { to: "/investment", label: "Investment Structure" },
  { to: "/financial-model", label: "Financial Model" },
];

const PRIMARY_LINKS = [
  { to: "/", label: "Overview" },
  { to: "/investment", label: "Investment" },
  { to: "/execution", label: "Execution" },
  { to: "/supply-chain", label: "Supply Chain" },
  { to: "/financial-model", label: "Model" },
  { to: "/solar", label: "Solar" },
  { to: "/seza", label: "SEZA" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const { pathname } = useLocation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setAccountOpen(false);
  }, [pathname]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#F9F9F6]/90 backdrop-blur-xl border-b border-[#E5E5E0]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 lg:px-16">
        <div className="flex items-center justify-between h-16 md:h-20 gap-6">
          <Link to="/" data-testid="brand-link" className="flex items-center gap-3 group flex-shrink-0">
            <div className="w-9 h-9 border border-[#1A1A1A] flex items-center justify-center">
              <span className="font-serif text-[15px] leading-none tracking-tight text-[#1A1A1A]">LRG</span>
            </div>
            <div className="hidden sm:flex flex-col leading-none">
              <span className="font-serif text-[15px] tracking-tight text-[#1A1A1A]">LRG Holdings</span>
              <span className="text-[10px] uppercase tracking-[0.22em] text-[#666666] mt-1">
                Botswana Leather
              </span>
            </div>
          </Link>

          <nav className="hidden xl:flex items-center gap-7 flex-1 justify-center">
            {PRIMARY_LINKS.map((r) => (
              <NavLink
                key={r.to}
                to={r.to}
                end={r.to === "/"}
                data-testid={`nav-link-${r.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `nav-link text-[12px] uppercase tracking-[0.18em] font-medium text-[#1A1A1A] ${
                    isActive ? "active" : ""
                  }`
                }
              >
                {r.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <Link
              to="/partner"
              data-testid="cta-nav-partner"
              className="px-4 py-2.5 border border-[#1A1A1A] text-[#1A1A1A] text-[11px] uppercase tracking-[0.18em] font-medium hover:bg-[#1A1A1A] hover:text-[#F9F9F6] transition-colors duration-300"
            >
              Partner
            </Link>
            {user && user !== false ? (
              <div className="relative">
                <button
                  data-testid="account-menu-toggle"
                  onClick={() => setAccountOpen((v) => !v)}
                  className="px-4 py-2.5 bg-[#5C2018] text-[#F9F9F6] text-[11px] uppercase tracking-[0.18em] font-medium hover:bg-[#1A1A1A] inline-flex items-center gap-2"
                >
                  {user.role === "admin" ? "Admin" : "Portal"}
                  <ChevronDown size={12} />
                </button>
                {accountOpen && (
                  <div
                    data-testid="account-menu"
                    className="absolute right-0 mt-2 min-w-[220px] bg-[#F9F9F6] border border-[#1A1A1A] py-2"
                  >
                    <div className="px-4 py-3 border-b border-[#E5E5E0]">
                      <p className="text-[10px] uppercase tracking-[0.2em] text-[#666666]">Signed in</p>
                      <p className="text-sm font-medium text-[#1A1A1A] truncate">{user.email}</p>
                    </div>
                    {user.role === "admin" ? (
                      <Link
                        to="/admin"
                        data-testid="account-menu-admin"
                        className="block px-4 py-2.5 text-sm text-[#1A1A1A] hover:bg-white"
                      >
                        Project Dashboard
                      </Link>
                    ) : (
                      <Link
                        to="/portal"
                        data-testid="account-menu-portal"
                        className="block px-4 py-2.5 text-sm text-[#1A1A1A] hover:bg-white"
                      >
                        Investor Portal
                      </Link>
                    )}
                    <button
                      onClick={handleLogout}
                      data-testid="account-menu-logout"
                      className="w-full text-left px-4 py-2.5 text-sm text-[#5C2018] hover:bg-white inline-flex items-center gap-2"
                    >
                      <LogOut size={14} /> Sign out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/login"
                data-testid="cta-investor-access"
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#5C2018] text-[#F9F9F6] text-[11px] uppercase tracking-[0.18em] font-medium hover:bg-[#1A1A1A] transition-colors duration-300"
              >
                Investor Access
                <span className="inline-block w-3 h-px bg-[#F9F9F6]" />
              </Link>
            )}
          </div>

          <button
            type="button"
            data-testid="mobile-menu-toggle"
            className="lg:hidden inline-flex items-center justify-center w-10 h-10 border border-[#1A1A1A]"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-[#F9F9F6] border-t border-[#E5E5E0] max-h-[80vh] overflow-y-auto">
          <div className="px-6 py-8 flex flex-col gap-2">
            <p className="eyebrow mb-2">Project</p>
            {PROJECT_LINKS.map((r) => (
              <NavLink
                key={r.to}
                to={r.to}
                end={r.to === "/"}
                data-testid={`mobile-nav-link-${r.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `font-serif text-2xl tracking-tight py-1 ${isActive ? "text-[#5C2018]" : "text-[#1A1A1A]"}`
                }
              >
                {r.label}
              </NavLink>
            ))}
            <p className="eyebrow mt-6 mb-2">Capital</p>
            {CAPITAL_LINKS.map((r) => (
              <NavLink
                key={r.to}
                to={r.to}
                data-testid={`mobile-nav-link-${r.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) =>
                  `font-serif text-2xl tracking-tight py-1 ${isActive ? "text-[#5C2018]" : "text-[#1A1A1A]"}`
                }
              >
                {r.label}
              </NavLink>
            ))}
            <p className="eyebrow mt-6 mb-2">Engage</p>
            <NavLink
              to="/partner"
              data-testid="mobile-nav-link-partner"
              className="font-serif text-2xl tracking-tight py-1 text-[#1A1A1A]"
            >
              Partner Onboarding
            </NavLink>
            <NavLink
              to="/contact"
              data-testid="mobile-nav-link-contact"
              className="font-serif text-2xl tracking-tight py-1 text-[#1A1A1A]"
            >
              Contact
            </NavLink>
            <div className="mt-6 grid grid-cols-1 gap-3">
              {user && user !== false ? (
                <>
                  <Link
                    to={user.role === "admin" ? "/admin" : "/portal"}
                    data-testid="mobile-portal-link"
                    className="px-5 py-3 bg-[#5C2018] text-[#F9F9F6] text-[12px] uppercase tracking-[0.18em] font-medium text-center"
                  >
                    {user.role === "admin" ? "Project Dashboard" : "Investor Portal"}
                  </Link>
                  <button
                    onClick={handleLogout}
                    data-testid="mobile-logout"
                    className="px-5 py-3 border border-[#1A1A1A] text-[#1A1A1A] text-[12px] uppercase tracking-[0.18em] font-medium"
                  >
                    Sign out
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    data-testid="mobile-cta-investor"
                    className="px-5 py-3 bg-[#5C2018] text-[#F9F9F6] text-[12px] uppercase tracking-[0.18em] font-medium text-center"
                  >
                    Investor Access
                  </Link>
                  <Link
                    to="/register"
                    data-testid="mobile-cta-register"
                    className="px-5 py-3 border border-[#1A1A1A] text-[#1A1A1A] text-[12px] uppercase tracking-[0.18em] font-medium text-center"
                  >
                    Request Access
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
